import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../../core/http/api.service';

export type CoachProductLine = 'HOBBIES' | 'MIND_FITNESS';
export type CoachAssignmentStatus = 'PENDING' | 'ACTIVE' | 'ENDED';
export type CoachConversionStatus = 'LEAD' | 'ENGAGED' | 'CONVERTED' | 'LOST';

export const PRODUCT_LINES: { value: CoachProductLine; label: string }[] = [
  { value: 'HOBBIES', label: 'Hobbies' },
  { value: 'MIND_FITNESS', label: 'Mind Fitness' }
];

export const CONVERSION_STATUSES: { value: CoachConversionStatus; label: string }[] = [
  { value: 'LEAD', label: 'Lead' },
  { value: 'ENGAGED', label: 'Engaged' },
  { value: 'CONVERTED', label: 'Converted' },
  { value: 'LOST', label: 'Lost' }
];

export const productLineLabel = (value?: string | null) =>
  PRODUCT_LINES.find(p => p.value === value)?.label ?? value ?? '';

export const conversionLabel = (value?: string | null) =>
  CONVERSION_STATUSES.find(c => c.value === value)?.label ?? value ?? '';

export interface CoachPerson {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  picture_url: string | null;
}

export interface CoachAssignment {
  id: string;
  product_line: CoachProductLine;
  status: CoachAssignmentStatus;
  conversion_status: CoachConversionStatus;
  assigned_at: string | null;
  engaged_at: string | null;
  converted_at: string | null;
  ended_at: string | null;
  end_reason: string | null;
  thread_id: string | null;
  created_on: string;
  user: CoachPerson | null;
  coach: CoachPerson | null;
}

export interface CoachMessage {
  id: string;
  thread_id: string;
  sender_id: string;
  content: string;
  type: string;
  /** SENT | DELIVERED | READ | FAILED (PENDING = local optimistic bubble) */
  status: string;
  /** IN_APP | WHATSAPP */
  channel?: string;
  template_name?: string | null;
  error_message?: string | null;
  created_on: string;
  sender?: CoachPerson | null;
  from_user?: boolean;
}

export interface InboxThread {
  thread_id: string;
  assignment: CoachAssignment;
  is_mine: boolean;
  last_message: CoachMessage | null;
  last_inbound_at: string | null;
  updated_on: string;
  unread_count: number;
}

export type WhatsAppUnavailableReason = 'DISABLED' | 'LINE_NOT_CONFIGURED' | 'NO_NUMBER' | 'OPTED_OUT' | 'NOT_VERIFIED';

export interface WhatsAppThreadState {
  available: boolean;
  reason: WhatsAppUnavailableReason | null;
  business_number: string | null;
  user_number: string | null;
  /** WhatsApp allows free text only within 24h of the user's last message */
  window_open: boolean;
  window_expires_at: string | null;
}

export type TemplateVarSource =
  | 'CLIENT_FIRST_NAME'
  | 'CLIENT_FULL_NAME'
  | 'COACH_FIRST_NAME'
  | 'COACH_FULL_NAME'
  | 'COACH_CALENDAR_LINK'
  | 'CUSTOM';

export const TEMPLATE_VAR_SOURCES: { value: TemplateVarSource; label: string }[] = [
  { value: 'CLIENT_FIRST_NAME', label: 'Client first name' },
  { value: 'CLIENT_FULL_NAME', label: 'Client full name' },
  { value: 'COACH_FIRST_NAME', label: 'Coach first name' },
  { value: 'COACH_FULL_NAME', label: 'Coach full name' },
  { value: 'COACH_CALENDAR_LINK', label: 'Coach calendar link' },
  { value: 'CUSTOM', label: 'Fixed / custom value' }
];

export interface TemplateVarSlot {
  id: string;
  component: 'HEADER' | 'BODY' | 'BUTTON';
  placeholder: number;
  /** Named Meta param, e.g. username / coach_name */
  parameter_name?: string | null;
  button_index?: number;
  button_text?: string | null;
  button_url?: string | null;
  context: string;
  example?: string | null;
  map_to: TemplateVarSource | null;
  custom_value?: string | null;
}

export interface WhatsAppTemplate {
  id: string;
  name: string;
  language: string;
  category: string | null;
  status: string;
  header_text: string | null;
  body_text: string;
  footer_text: string | null;
  param_count: number;
  variables?: TemplateVarSlot[];
  product_line: CoachProductLine | null;
  is_enabled: boolean;
  /** When true, auto-sent the first time a coach is assigned (system fills mapped vars). */
  auto_send_on_assign: boolean;
  synced_at: string | null;
  /** Filled when listing templates for a thread */
  suggested_values?: Record<string, string>;
}

export interface ThreadTemplates {
  whatsapp: WhatsAppThreadState;
  templates: WhatsAppTemplate[];
  var_sources?: { value: TemplateVarSource; label: string }[];
}

export interface WhatsAppStatus {
  enabled: boolean;
  api_version: string;
  has_access_token: boolean;
  has_app_secret: boolean;
  has_webhook_verify_token: boolean;
  has_business_account_id: boolean;
  lines: { product_line: CoachProductLine; configured: boolean; display_number: string | null }[];
}

/** Preview with variables resolved by slot id (header/body/buttons). */
export const renderTemplate = (
  t: Pick<WhatsAppTemplate, 'header_text' | 'body_text' | 'footer_text' | 'variables'>,
  valuesById: Record<string, string>
) => {
  const slots = t.variables || [];
  const fill = (text: string, component: TemplateVarSlot['component']) =>
    (text || '').replace(/\{\{\s*([A-Za-z_][\w]*|\d+)\s*\}\}/g, (m, token) => {
      const slot = slots.find(s => {
        if (s.component !== component) {
          return false;
        }
        if (s.parameter_name) {
          return s.parameter_name === token;
        }
        return String(s.placeholder) === token;
      });
      return (slot && valuesById[slot.id]?.trim()) || m;
    });
  const parts = [
    t.header_text ? fill(t.header_text, 'HEADER') : null,
    fill(t.body_text || '', 'BODY'),
    t.footer_text || null
  ].filter(Boolean) as string[];
  for (const s of slots.filter(x => x.component === 'BUTTON')) {
    const token = s.parameter_name || String(s.placeholder);
    const url = (s.button_url || '').replace(`{{${token}}}`, valuesById[s.id] || `{{${token}}}`);
    parts.push(`[${s.button_text || 'Button'}] ${url}`);
  }
  return parts.join('\n\n');
};

export interface ThreadDetail {
  thread_id: string;
  assignment: CoachAssignment;
  is_mine: boolean;
  can_reply: boolean;
  last_inbound_at: string | null;
  has_more: boolean;
  messages: CoachMessage[];
  whatsapp?: WhatsAppThreadState;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CoachPoolEntry {
  id: string;
  product_line: CoachProductLine;
  is_accepting: boolean;
  max_active_clients: number | null;
  active_client_count: number;
  last_assigned_at: string | null;
  calendar_link?: string | null;
  coach: CoachPerson | null;
}

export interface CoachStatsRow {
  coach: { id: string; name: string; email: string; picture_url: string | null };
  product_line: CoachProductLine;
  active_clients: number;
  leads: number;
  engaged: number;
  lost: number;
  converted: number;
  total_assigned: number;
  conversion_rate: number;
}

export interface CoachStats {
  coaches: CoachStatsRow[];
  pending: Partial<Record<CoachProductLine, number>>;
}

export interface MyCoach {
  product_line: CoachProductLine;
  product_line_label: string;
  status: CoachAssignmentStatus | null;
  thread_id: string | null;
  assigned_at: string | null;
  coach: CoachPerson | null;
  unread_count?: number;
}

export interface UnknownWhatsAppMessage {
  id: string;
  content: string;
  sent_at: string;
  external_id: string;
}

export interface UnknownWhatsAppSender {
  phone_digits: string;
  display_number: string;
  product_line: CoachProductLine;
  message_count: number;
  last_content: string;
  last_sent_at: string | null;
  messages: UnknownWhatsAppMessage[];
}

export interface UserSearchResult {
  user: CoachPerson;
  lines: {
    product_line: CoachProductLine;
    status: CoachAssignmentStatus | null;
    assignment_id: string | null;
    thread_id: string | null;
    coach: CoachPerson | null;
  }[];
}

export interface StartConversationResult {
  thread_id: string;
  created: boolean;
  is_mine: boolean;
  assignment: CoachAssignment;
}

export interface InboxQuery {
  scope?: 'mine' | 'all';
  coach_id?: string;
  product_line?: CoachProductLine | '';
  conversion_status?: CoachConversionStatus | '';
  unread?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

/** 'coach' → /manage-trainer/coaching, 'admin' → /admin/coaching */
export type InboxMode = 'coach' | 'admin';

const clean = (params: Record<string, any>) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== false));

@Injectable({ providedIn: 'root' })
export class CoachingService {
  private readonly COACH = 'manage-trainer/coaching';
  private readonly ADMIN = 'admin/coaching';
  private readonly USER = 'user/coaches';

  constructor(private api: ApiService) { }

  private base(mode: InboxMode) {
    return mode === 'admin' ? this.ADMIN : this.COACH;
  }

  // ---- inbox (coach + admin)
  listThreads(mode: InboxMode, query: InboxQuery): Observable<Paged<InboxThread>> {
    return this.api.get<Paged<InboxThread>>(`${this.base(mode)}/threads`, clean(query));
  }

  getThread(mode: InboxMode, threadId: string, before?: string): Observable<ThreadDetail> {
    return this.api.get<ThreadDetail>(`${this.base(mode)}/threads/${threadId}`, clean({ before }));
  }

  /** `in_app_only` asks the API to skip the WhatsApp leg (needs API support, S7). */
  sendMessage(mode: InboxMode, threadId: string, content: string, opts: { in_app_only?: boolean } = {}): Observable<CoachMessage> {
    return this.api.post<CoachMessage>(`${this.base(mode)}/threads/${threadId}/messages`, { content, ...clean(opts) });
  }

  searchUsers(mode: InboxMode, search: string): Observable<UserSearchResult[]> {
    return this.api.get<UserSearchResult[]>(`${this.base(mode)}/users`, clean({ search }));
  }

  startConversation(mode: InboxMode, data: { user_id: string; product_line: CoachProductLine; coach_user_id?: string }) {
    return this.api.post<StartConversationResult>(`${this.base(mode)}/conversations`, clean(data));
  }

  getThreadTemplates(mode: InboxMode, threadId: string): Observable<ThreadTemplates> {
    return this.api.get<ThreadTemplates>(`${this.base(mode)}/threads/${threadId}/templates`);
  }

  sendTemplate(
    mode: InboxMode,
    threadId: string,
    templateId: string,
    params?: string[] | Record<string, string>
  ): Observable<CoachMessage> {
    return this.api.post<CoachMessage>(`${this.base(mode)}/threads/${threadId}/template`, {
      template_id: templateId,
      params: params ?? {}
    });
  }

  getWhatsAppStatus(): Observable<WhatsAppStatus> {
    return this.api.get<WhatsAppStatus>(`${this.ADMIN}/whatsapp/status`);
  }

  listWhatsAppTemplates(): Observable<WhatsAppTemplate[]> {
    return this.api.get<WhatsAppTemplate[]>(`${this.ADMIN}/whatsapp/templates`);
  }

  syncWhatsAppTemplates(): Observable<{ synced: number; removed: number; templates: WhatsAppTemplate[] }> {
    return this.api.post(`${this.ADMIN}/whatsapp/templates/sync`, {});
  }

  updateWhatsAppTemplate(
    id: string,
    data: {
      product_line?: CoachProductLine | null;
      is_enabled?: boolean;
      auto_send_on_assign?: boolean;
      variables?: Array<{ id: string; map_to?: TemplateVarSource | null; custom_value?: string | null }>;
    }
  ): Observable<WhatsAppTemplate> {
    return this.api.put<WhatsAppTemplate>(`${this.ADMIN}/whatsapp/templates/${id}`, data);
  }

  markRead(threadId: string, mode: InboxMode = 'coach'): Observable<{ success: boolean }> {
    return this.api.post(`${this.base(mode)}/threads/${threadId}/read`, {});
  }

  getUnreadTotal(mode: InboxMode = 'coach'): Observable<{ unread: number }> {
    return this.api.get(`${this.base(mode)}/threads/unread-count`);
  }

  listUnknownWhatsApp(mode: InboxMode, productLine?: CoachProductLine | ''): Observable<{ items: UnknownWhatsAppSender[]; total: number }> {
    return this.api.get(`${this.base(mode)}/unknown-whatsapp`, clean({ product_line: productLine }));
  }

  getUnknownWhatsAppCount(mode: InboxMode): Observable<{ count: number }> {
    return this.api.get(`${this.base(mode)}/unknown-whatsapp/count`);
  }

  linkUnknownWhatsApp(
    mode: InboxMode,
    data: { phone_digits: string; user_id: string; product_line?: CoachProductLine }
  ): Observable<{
    success: boolean;
    thread_id: string;
    user_id: string;
    product_line: CoachProductLine;
    messages_ingested: number;
  }> {
    return this.api.post(`${this.base(mode)}/unknown-whatsapp/link`, clean(data));
  }

  dismissUnknownWhatsApp(
    mode: InboxMode,
    data: { phone_digits: string; product_line?: CoachProductLine }
  ): Observable<{ success: boolean; dismissed: number }> {
    return this.api.post(`${this.base(mode)}/unknown-whatsapp/dismiss`, clean(data));
  }

  // ---- coach
  listMyClients(query: { product_line?: string; conversion_status?: string; search?: string; page?: number; limit?: number }) {
    return this.api.get<Paged<CoachAssignment>>(`${this.COACH}/clients`, clean(query));
  }

  setConversionStatus(mode: InboxMode, assignmentId: string, status: CoachConversionStatus): Observable<CoachAssignment> {
    const path = mode === 'admin'
      ? `${this.ADMIN}/assignments/${assignmentId}/conversion`
      : `${this.COACH}/clients/${assignmentId}/conversion`;
    return this.api.put<CoachAssignment>(path, { conversion_status: status });
  }

  getMyAvailability(): Observable<CoachPoolEntry[]> {
    return this.api.get<CoachPoolEntry[]>(`${this.COACH}/availability`);
  }

  setMyAvailability(
    data: { is_accepting?: boolean; calendar_link?: string | null; product_line?: CoachProductLine }
  ): Observable<CoachPoolEntry[]> {
    return this.api.put<CoachPoolEntry[]>(`${this.COACH}/availability`, clean(data));
  }

  // ---- admin
  listPool(productLine?: CoachProductLine | ''): Observable<CoachPoolEntry[]> {
    return this.api.get<CoachPoolEntry[]>(`${this.ADMIN}/pool`, clean({ product_line: productLine }));
  }

  listEligibleCoaches(search?: string): Observable<CoachPerson[]> {
    return this.api.get<CoachPerson[]>(`${this.ADMIN}/pool/eligible-coaches`, clean({ search }));
  }

  addToPool(trainerUserId: string, productLine: CoachProductLine): Observable<CoachPoolEntry> {
    return this.api.post<CoachPoolEntry>(`${this.ADMIN}/pool`, { trainer_user_id: trainerUserId, product_line: productLine });
  }

  updatePoolEntry(
    id: string,
    data: { is_accepting?: boolean; max_active_clients?: number | null; calendar_link?: string | null }
  ): Observable<CoachPoolEntry> {
    return this.api.put<CoachPoolEntry>(`${this.ADMIN}/pool/${id}`, data);
  }

  removeFromPool(id: string): Observable<{ success: boolean }> {
    return this.api.delete(`${this.ADMIN}/pool/${id}`);
  }

  listAssignments(query: {
    status?: CoachAssignmentStatus | ''; product_line?: string; coach_id?: string;
    conversion_status?: string; search?: string; page?: number; limit?: number;
  }): Observable<Paged<CoachAssignment>> {
    return this.api.get<Paged<CoachAssignment>>(`${this.ADMIN}/assignments`, clean(query));
  }

  reassign(assignmentId: string, coachUserId?: string): Observable<CoachAssignment> {
    return this.api.put<CoachAssignment>(`${this.ADMIN}/assignments/${assignmentId}/reassign`, clean({ coach_user_id: coachUserId }));
  }

  fillPending(productLine: CoachProductLine): Observable<{ filled: number; remaining: number }> {
    return this.api.post(`${this.ADMIN}/assignments/fill-pending`, { product_line: productLine });
  }

  backfill(productLine: CoachProductLine, limit = 200): Observable<{ processed: number; assigned: number; pending: number }> {
    return this.api.post(`${this.ADMIN}/assignments/backfill`, { product_line: productLine, limit });
  }

  getStats(productLine?: CoachProductLine | ''): Observable<CoachStats> {
    return this.api.get<CoachStats>(`${this.ADMIN}/stats`, clean({ product_line: productLine }));
  }

  // ---- user
  getMyCoaches(): Observable<MyCoach[]> {
    return this.api.get<MyCoach[]>(this.USER);
  }

  getMyUnreadTotal(): Observable<{ unread: number }> {
    return this.api.get<{ unread: number }>(`${this.USER}/unread-count`);
  }

  getMyCoachThread(threadId: string, before?: string): Observable<ThreadDetail> {
    return this.api.get<ThreadDetail>(`${this.USER}/threads/${threadId}`, clean({ before }));
  }

  sendToMyCoach(threadId: string, content: string): Observable<CoachMessage> {
    return this.api.post<CoachMessage>(`${this.USER}/threads/${threadId}/messages`, { content });
  }
}
